import path from 'node:path';
import { existsSync, mkdtempSync } from 'node:fs';
import { homedir, tmpdir } from 'node:os';
import { mkdir, readFile, rm } from 'node:fs/promises';
import { omit, snakeCase } from 'lodash-es';
import sanitize from 'sanitize-filename';
import StreamZip from 'node-stream-zip';
import type { ReportingOptions } from '../reporting/types.ts';
import { status } from '../reporting/report.ts';
import { GtfsError, GtfsErrorCategory, GtfsErrorCode } from './errors.ts';

const homeDirectory = homedir();

/** Configuration command line arguments interface */
interface ConfigArgs {
  configPath?: string;
  gtfsPath?: string;
  gtfsUrl?: string;
  sqlitePath?: string;
}

/**
 * Attempts to parse and load configuration from various sources
 * Priority: 1. CLI config path 2. CLI direct args 3. ./config.json
 * @param {ConfigArgs} argv - Command line arguments
 * @throws {Error} If configuration cannot be found or parsed
 * @returns {Promise<Record<string, any>>} Parsed configuration object
 * @example
 * const config = await getConfig({ configPath: './my-config.json' });
 */
export async function getConfig<Config extends ReportingOptions>(
  argv: ConfigArgs,
): Promise<Config> {
  let config;
  let data;

  try {
    if (argv.configPath) {
      const configPath = path.resolve(untildify(argv.configPath));

      try {
        data = await readFile(configPath, 'utf8');
      } catch (error) {
        throw new GtfsError(
          `Cannot find configuration file at \`${argv.configPath}\`. Use config-sample.json as a starting point, pass --configPath option.`,
          {
            code: GtfsErrorCode.GTFS_CONFIG_FILE_NOT_FOUND,
            category: GtfsErrorCategory.CONFIG,
            details: { configPath: argv.configPath },
            cause: error,
          },
        );
      }

      config = Object.assign(JSON.parse(data), argv);
    } else if (argv.gtfsPath || argv.gtfsUrl || argv.sqlitePath) {
      const agencies = [
        ...(argv.gtfsPath ? [{ path: argv.gtfsPath }] : []),
        ...(argv.gtfsUrl ? [{ url: argv.gtfsUrl }] : []),
      ];

      config = {
        agencies,
        ...omit(argv, ['gtfsPath', 'gtfsUrl']),
      };
    } else if (existsSync(path.resolve('./config.json'))) {
      data = await readFile(path.resolve('./config.json'), 'utf8');
      config = Object.assign(JSON.parse(data), argv);
      status(config, 'Using configuration from ./config.json');
    } else {
      throw new GtfsError(
        'Cannot find configuration file. Use config-sample.json as a starting point, pass --configPath option.',
        {
          code: GtfsErrorCode.GTFS_CONFIG_FILE_NOT_FOUND,
          category: GtfsErrorCategory.CONFIG,
        },
      );
    }

    return config as Config;
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new GtfsError(
        `Cannot parse configuration file. Check to ensure that it is valid JSON. Error: ${error.message}`,
        {
          code: GtfsErrorCode.GTFS_CONFIG_PARSE_FAILED,
          category: GtfsErrorCategory.CONFIG,
          cause: error,
        },
      );
    }
    throw error;
  }
}

/**
 * Prepares a directory for saving files by clearing its contents
 * @param {string} exportPath - Path to the directory to prepare
 * @returns {Promise<void>}
 * @example
 * await prepDirectory('./output');
 */
export async function prepDirectory(exportPath: string): Promise<void> {
  await rm(exportPath, { recursive: true, force: true });
  await mkdir(exportPath, { recursive: true });
}

/**
 * Extracts contents of a zip file to specified directory
 * @param {string} zipfilePath - Path to the zip file
 * @param {string} exportPath - Directory to extract contents to
 * @returns {Promise<void>}
 * @throws {Error} If zip file cannot be opened or extracted
 * @example
 * await unzip('./data.zip', './extracted');
 */
export async function unzip(
  zipfilePath: string,
  exportPath: string,
): Promise<void> {
  let zip: StreamZip.StreamZipAsync | undefined;
  let extractionError: unknown;

  try {
    zip = new StreamZip.async({ file: zipfilePath });
    await zip.extract(null, exportPath);
  } catch (error) {
    extractionError = error;
  } finally {
    try {
      await zip?.close();
    } catch (error) {
      extractionError ??= error;
    }
  }

  if (extractionError) {
    throw new Error(
      `Failed to extract zip file: ${extractionError instanceof Error ? extractionError.message : 'Unknown error'}`,
      { cause: extractionError },
    );
  }
}

/**
 * Generates a safe folder name from input string
 * Converts to snake_case and removes unsafe characters
 * @param {string} folderName - Input string to convert to folder name
 * @returns {string} Sanitized folder name
 * @example
 * generateFolderName('My Folder!') // returns 'my_folder'
 */
export function generateFolderName(folderName: string): string {
  if (!folderName || typeof folderName !== 'string') {
    throw new Error('Folder name must be a non-empty string');
  }
  return snakeCase(sanitize(folderName));
}

/**
 * Converts a tilde path to a full path
 * @param pathWithTilde The path to convert
 * @returns The full path
 */
export function untildify(pathWithTilde: string): string {
  return homeDirectory
    ? pathWithTilde.replace(/^~(?=$|\/|\\)/, homeDirectory)
    : pathWithTilde;
}

/**
 * Creates a new, unique temporary directory and returns its path
 * @returns {string} Path to the newly created temporary directory
 */
export function temporaryDirectory(): string {
  return mkdtempSync(path.join(tmpdir(), 'gtfs-'));
}
